// ============================================================================
//  MIS PETICIONES (panel de lealtad) — F2 del sistema de peticiones, 11-oct-2026. Lo que el negocio le ha
//  pedido a Lealify —arreglos, mejoras, dudas— con número, estado y lo que se hizo. Antes solo existía en
//  el WhatsApp de Carlos y se repetía («¿me debes algo?»). Aquí el negocio:
//    · ve sus peticiones en curso y las ya resueltas, con el historial;
//    · abre una nueva (tipo + título + detalle + captura opcional) → nos llega por WhatsApp;
//    · contesta cuando le pedimos un dato («Esperando tu respuesta»).
//  Port de la agenda (agenda/src/components/ayuda/PeticionesTab.tsx) con tokens del manager.
//  Entrada PROPIA en el menú (no pestaña de Preguntas Frecuentes). API: api/clientRequestsApi.ts.
//  Plan: forobeta-ops/docs/lealify-peticiones-plan-20261010.md §4.
// ============================================================================
import React, { useCallback, useEffect, useState } from 'react';
import {
  ArrowLeft, Bug, CalendarClock, CheckCircle2, ExternalLink, HelpCircle, ImagePlus, Lightbulb,
  Inbox, Loader2, Plus, Send, X,
} from 'lucide-react';
import Layout from '../../../components/layout/Layout';
import PageHeader from '../../../components/ui/PageHeader';
import {
  ClientRequest, ClientRequestDetail, RequestKind, RequestStatus,
  clientRequestsApi, uploadRequestScreenshot, useClientRequestsInfo,
} from '../../../api/clientRequestsApi';

const STATUS_CLS: Record<RequestStatus, string> = {
  nueva: 'bg-surface-2 text-content-2 border border-line',
  revision: 'bg-sky-500/15 text-sky-700 dark:text-sky-300',
  curso: 'bg-accent/10 text-accent',
  esperando: 'bg-amber-500/15 text-amber-700 dark:text-amber-300',
  hecha: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
  descartada: 'bg-surface-2 text-content-3 border border-line',
};

const TIPOS: { k: RequestKind; label: string; Icon: React.ElementType; ayuda: string; ejemplo: string }[] = [
  { k: 'bug', label: 'Algo no funciona', Icon: Bug, ayuda: 'Dinos qué pasó, en qué pantalla y con qué cliente o visita.', ejemplo: 'Ej. No me deja canjear una recompensa' },
  { k: 'mejora', label: 'Quiero una mejora', Icon: Lightbulb, ayuda: 'Cuéntanos qué te ahorraría tiempo y cómo lo haces hoy.', ejemplo: 'Ej. Que el mensaje de cumpleaños salga un día antes' },
  { k: 'duda', label: 'Tengo una duda', Icon: HelpCircle, ayuda: 'Si es de cómo usar algo, prueba primero en «Preguntas Frecuentes».', ejemplo: 'Ej. ¿Cómo agrego a un cajero nuevo?' },
];

const inputCls = 'w-full bg-surface-2 text-content rounded-xl px-3 py-2.5 text-sm border border-line placeholder:text-content-3 focus:outline-none focus:ring-2 focus:ring-accent/40';
const btnPrimary = 'inline-flex items-center justify-center gap-2 rounded-xl bg-accent-solid px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-accent-solid/90 disabled:bg-line disabled:text-content-3';
const card = 'rounded-2xl border border-line bg-surface p-4 shadow-sm';
const cerrada = (s: RequestStatus) => s === 'hecha' || s === 'descartada';

function fecha(s: string | null | undefined, conHora = false): string {
  if (!s) return '';
  // «2026-10-15» suelto se leería como medianoche UTC (= día anterior en México): mediodía local.
  const t = String(s);
  const d = new Date(/^\d{4}-\d{2}-\d{2}$/.test(t) ? `${t}T12:00:00` : t.replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return String(s).slice(0, 10);
  return d.toLocaleDateString('es-MX', { day: 'numeric', month: 'short', ...(conHora ? { hour: 'numeric', minute: '2-digit' } : {}) });
}

const Estado: React.FC<{ r: { status: RequestStatus; statusLabel: string } }> = ({ r }) => (
  <span className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS_CLS[r.status] || STATUS_CLS.nueva}`}>
    {r.statusLabel}
  </span>
);

// ── Formulario «Nueva petición» ─────────────────────────────────────────────
const Nueva: React.FC<{ onCancel: () => void; onCreated: (r: ClientRequest) => void }> = ({ onCancel, onCreated }) => {
  const [kind, setKind] = useState<RequestKind>('bug');
  const [title, setTitle] = useState('');
  const [detail, setDetail] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!file) { setPreview(null); return undefined; }
    const u = URL.createObjectURL(file);
    setPreview(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);

  const enviar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (enviando) return;
    setError(null);
    if (title.trim().length < 4) { setError('Escribe en una frase qué necesitas.'); return; }
    setEnviando(true);
    try {
      let attachmentUrl: string | null = null;
      if (file) attachmentUrl = await uploadRequestScreenshot(file);
      const r = await clientRequestsApi.create({ title: title.trim(), detail: detail.trim() || undefined, kind, attachmentUrl });
      onCreated(r);
    } catch (err) {
      setError((err as Error).message || 'No se pudo enviar. Intenta de nuevo.');
    } finally {
      setEnviando(false);
    }
  };

  const tipo = TIPOS.find((t) => t.k === kind);
  return (
    <form onSubmit={enviar} className={card} data-testid="peticion-form">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-base font-semibold text-content">Nueva petición</h3>
        <button type="button" onClick={onCancel} className="rounded-lg p-1.5 text-content-3 hover:bg-surface-2" aria-label="Cerrar"><X size={18} /></button>
      </div>

      <div className="mb-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
        {TIPOS.map(({ k, label, Icon }) => (
          <button key={k} type="button" onClick={() => setKind(k)} aria-pressed={kind === k}
            className={`flex items-center gap-2 rounded-xl border px-3 py-2.5 text-left text-sm font-medium transition ${kind === k ? 'border-accent/50 bg-accent/10 text-accent' : 'border-line text-content-2 hover:bg-surface-2 hover:text-content'}`}>
            <Icon size={16} /> {label}
          </button>
        ))}
      </div>

      <label className="mb-1 block text-xs font-semibold text-content-2" htmlFor="pet-titulo">¿Qué necesitas? (una frase)</label>
      <input id="pet-titulo" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={160} disabled={enviando}
        placeholder={tipo?.ejemplo} className={`${inputCls} mb-3`} />

      <label className="mb-1 block text-xs font-semibold text-content-2" htmlFor="pet-detalle">Detalle <span className="font-normal text-content-3">(opcional)</span></label>
      <textarea id="pet-detalle" value={detail} onChange={(e) => setDetail(e.target.value)} maxLength={4000} rows={4} disabled={enviando}
        placeholder={tipo?.ayuda} className={`${inputCls} mb-3 resize-y`} />

      <div className="mb-3 flex flex-wrap items-center gap-3">
        <label className="inline-flex cursor-pointer items-center gap-2 rounded-xl border border-dashed border-line px-3 py-2 text-sm text-content-2 hover:bg-surface-2">
          <ImagePlus size={16} /> {file ? 'Cambiar captura' : 'Adjuntar captura (opcional)'}
          {/* Sin `capture`: en móvil escondería la galería (y la captura de pantalla vive ahí). */}
          <input type="file" accept="image/*" className="hidden" disabled={enviando}
            onChange={(e) => {
              const f = e.target.files?.[0] || null; e.target.value = '';
              if (f && f.size > 8 * 1024 * 1024) { setError('La imagen pesa más de 8 MB.'); return; }
              setError(null); setFile(f);
            }} />
        </label>
        {preview && (
          <div className="relative">
            <img src={preview} alt="Captura adjunta" className="h-16 w-auto rounded-lg border border-line object-cover" />
            <button type="button" onClick={() => setFile(null)} className="absolute -right-2 -top-2 rounded-full border border-line bg-surface p-0.5 text-content-2 shadow" aria-label="Quitar captura"><X size={14} /></button>
          </div>
        )}
      </div>

      {error && <p className="mb-3 rounded-lg bg-rose-500/10 px-3 py-2 text-sm text-rose-600 dark:text-rose-300">{error}</p>}

      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs text-content-3">Nos llega al momento. Te contestamos aquí y, si hace falta, por WhatsApp.</p>
        <button type="submit" disabled={enviando || title.trim().length < 4} className={btnPrimary} data-testid="peticion-enviar">
          {enviando ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Enviar petición
        </button>
      </div>
    </form>
  );
};

// ── Detalle con historial y respuesta ───────────────────────────────────────
const Detalle: React.FC<{ id: number; onBack: () => void; onChanged: () => void }> = ({ id, onBack, onChanged }) => {
  const [r, setR] = useState<ClientRequestDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);

  const cargar = useCallback(() => {
    clientRequestsApi.get(id).then(setR).catch((e) => setError((e as Error).message));
  }, [id]);
  useEffect(() => { cargar(); }, [cargar]);

  const responder = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!texto.trim() || enviando) return;
    setEnviando(true); setError(null);
    try {
      await clientRequestsApi.reply(id, texto.trim());
      setTexto('');
      cargar();
      onChanged();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setEnviando(false);
    }
  };

  const volver = (
    <button type="button" onClick={onBack} className="mb-3 inline-flex items-center gap-1 text-sm text-content-3 hover:text-content">
      <ArrowLeft size={16} /> Mis peticiones
    </button>
  );
  if (error && !r) return <div className={card}>{volver}<p className="rounded-xl bg-rose-500/10 px-3 py-2 text-sm text-rose-600 dark:text-rose-300">{error}</p></div>;
  if (!r) return <div className="flex justify-center py-10 text-content-3"><Loader2 className="animate-spin" /></div>;
  const abierta = !cerrada(r.status);

  return (
    <div className={card} data-testid="peticion-detalle">
      {volver}
      {/* Móvil: estado arriba y título a todo lo ancho; desde sm el estado vuelve a la derecha. */}
      <div className="flex flex-col-reverse items-start gap-2 sm:flex-row sm:justify-between">
        <div className="min-w-0">
          <p className="text-xs text-content-3">#{r.id} · {r.kindLabel} · {fecha(r.requested_at || r.created_at)}{r.requested_by ? ` · ${r.requested_by}` : ''}</p>
          <h3 className="mt-0.5 break-words text-lg font-semibold text-content">{r.title}</h3>
        </div>
        <Estado r={r} />
      </div>

      {r.status === 'esperando' && (
        <p className="mt-3 rounded-xl bg-amber-500/15 px-3 py-2 text-sm text-amber-700 dark:text-amber-300">Te pedimos un dato para poder seguir. Contéstanos abajo.</p>
      )}
      {r.eta_date && abierta && (
        <p className="mt-3 inline-flex items-center gap-1.5 text-sm text-content-2"><CalendarClock size={15} /> Lo tenemos para el {fecha(r.eta_date)}.</p>
      )}
      {r.detail && <p className="mt-3 whitespace-pre-wrap break-words text-sm text-content-2">{r.detail}</p>}
      {r.attachment_url && (
        <a href={r.attachment_url} target="_blank" rel="noreferrer" className="mt-3 block w-fit">
          <img src={r.attachment_url} alt="Captura" className="max-h-48 max-w-full rounded-lg border border-line" />
        </a>
      )}
      {r.resolution && (
        // En «esperando», resolution = lo que le pedimos al cliente (11-oct-2026).
        <div className={`mt-4 rounded-xl p-3 ${r.status === 'esperando' ? 'bg-amber-500/10' : 'bg-emerald-500/10'}`}>
          <p className={`flex items-center gap-1.5 text-sm font-semibold ${r.status === 'esperando' ? 'text-amber-700 dark:text-amber-300' : 'text-emerald-700 dark:text-emerald-300'}`}><CheckCircle2 size={16} /> {r.status === 'esperando' ? 'Lo que te pedimos' : r.status === 'descartada' ? 'Por qué no por ahora' : 'Lo que hicimos'}</p>
          <p className="mt-1 whitespace-pre-wrap break-words text-sm text-content">{r.resolution}</p>
        </div>
      )}
      {r.doc_url && (
        <a href={r.doc_url} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-accent hover:underline">
          <ExternalLink size={15} /> Ver la guía
        </a>
      )}

      {r.events.length > 0 && (
        <div className="mt-5">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-content-3">Historial</p>
          <ol className="space-y-2 border-l border-line pl-4">
            {r.events.map((e, i) => (
              <li key={i} className="relative">
                <span className={`absolute -left-[21px] top-1.5 h-2.5 w-2.5 rounded-full ${e.actor === 'cliente' ? 'bg-accent' : 'bg-content-3'}`} />
                <p className="text-xs text-content-3">{fecha(e.at, true)} · {e.actor === 'cliente' ? 'Tu negocio' : 'Lealify'}{e.statusLabel ? ` · ${e.statusLabel}` : ''}</p>
                {e.note && <p className="whitespace-pre-wrap break-words text-sm text-content">{e.note}</p>}
              </li>
            ))}
          </ol>
        </div>
      )}

      {abierta && (
        <form onSubmit={responder} className="mt-5 flex items-end gap-2">
          <textarea value={texto} onChange={(e) => setTexto(e.target.value)} rows={2} maxLength={4000} disabled={enviando}
            placeholder={r.status === 'esperando' ? 'Escribe el dato que te pedimos…' : 'Agrega un comentario o un dato…'}
            className={`${inputCls} flex-1 resize-y !py-2`} data-testid="peticion-respuesta" />
          <button type="submit" disabled={enviando || !texto.trim()} aria-label="Enviar respuesta"
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent-solid text-white transition hover:bg-accent-solid/90 disabled:bg-line disabled:text-content-3">
            {enviando ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
          </button>
        </form>
      )}
      {error && r && <p className="mt-2 text-sm text-rose-600 dark:text-rose-300">{error}</p>}
    </div>
  );
};

// ── Página ──────────────────────────────────────────────────────────────────
const ClientRequestsInner: React.FC = () => {
  const [filtro, setFiltro] = useState<'abiertas' | 'hechas'>('abiertas');
  const [lista, setLista] = useState<ClientRequest[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [nueva, setNueva] = useState(false);
  const [abierta, setAbierta] = useState<number | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const cargar = useCallback(() => {
    setError(null);
    clientRequestsApi.list(filtro).then(setLista).catch((e) => setError((e as Error).message));
  }, [filtro]);
  useEffect(() => { setLista(null); cargar(); }, [cargar]);

  let cuerpo: React.ReactNode;
  if (abierta !== null) {
    cuerpo = <Detalle id={abierta} onBack={() => { setAbierta(null); cargar(); }} onChanged={cargar} />;
  } else {
    cuerpo = (
      <div className="flex w-full flex-col gap-3" data-testid="peticiones-lista">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex gap-1 rounded-xl border border-line bg-surface-2 p-1" role="tablist">
            {(['abiertas', 'hechas'] as const).map((f) => (
              <button key={f} type="button" role="tab" aria-selected={filtro === f} onClick={() => setFiltro(f)}
                className={`rounded-lg px-3 py-1.5 text-sm font-medium transition ${filtro === f ? 'bg-surface text-content shadow-sm' : 'text-content-3 hover:text-content'}`}>
                {f === 'abiertas' ? 'En curso' : 'Resueltas'}
              </button>
            ))}
          </div>
          {!nueva && (
            <button type="button" onClick={() => { setNueva(true); setAviso(null); }} className={`${btnPrimary} !px-3.5 !py-2`} data-testid="peticion-nueva">
              <Plus size={16} /> Nueva petición
            </button>
          )}
        </div>

        {aviso && <p className="rounded-xl bg-emerald-500/10 px-3 py-2 text-sm text-emerald-700 dark:text-emerald-300">{aviso}</p>}

        {nueva && (
          <Nueva onCancel={() => setNueva(false)}
            onCreated={(r) => { setNueva(false); setAviso(`Listo, recibimos tu petición #${r.id}. Te contestamos aquí.`); if (filtro === 'abiertas') cargar(); else setFiltro('abiertas'); }} />
        )}

        {error && <p className="rounded-xl bg-rose-500/10 px-3 py-2 text-sm text-rose-600 dark:text-rose-300">{error}</p>}
        {lista === null && !error && <div className="flex justify-center py-10 text-content-3"><Loader2 className="animate-spin" /></div>}

        {lista && lista.length === 0 && (
          <div className="flex flex-col items-center rounded-2xl border border-dashed border-line bg-surface px-4 py-10 text-center text-content-3" data-testid="peticiones-vacio">
            <Inbox size={30} className="mb-2 opacity-50" />
            <p className="text-sm">{filtro === 'abiertas' ? 'No tienes peticiones en curso.' : 'Todavía no hay peticiones resueltas.'}</p>
            {filtro === 'abiertas' && <p className="mt-1 max-w-sm text-xs">Si algo no funciona o quieres una mejora, toca «Nueva petición». Lo que nos pidas por WhatsApp también aparece aquí.</p>}
          </div>
        )}

        {lista && lista.length > 0 && (
          <ul className="space-y-2">
            {lista.map((r) => (
              <li key={r.id}>
                <button type="button" onClick={() => setAbierta(r.id)} data-testid="peticion-item"
                  className={`flex w-full flex-col-reverse items-start gap-1.5 rounded-2xl border bg-surface p-3 text-left shadow-sm transition hover:border-accent/40 sm:flex-row sm:items-center sm:gap-3 ${r.status === 'esperando' ? 'border-amber-500/50' : 'border-line'}`}>
                  {/* Móvil: la etiqueta de estado va ARRIBA y el título a todo lo ancho (en la agenda, a 390 px,
                      el título se cortaba en «Landing / siti…»); desde sm vuelve a la derecha. */}
                  <div className="w-full min-w-0 sm:w-auto sm:flex-1">
                    <p className="line-clamp-2 break-words text-sm font-semibold text-content sm:line-clamp-1">{r.title}</p>
                    <p className="mt-0.5 text-xs text-content-3">
                      #{r.id} · {r.kindLabel} · {fecha(r.requested_at || r.created_at)}
                      {r.eta_date && !cerrada(r.status) ? ` · para el ${fecha(r.eta_date)}` : ''}
                      {r.resolved_at && cerrada(r.status) ? ` · cerrada el ${fecha(r.resolved_at)}` : ''}
                    </p>
                  </div>
                  <Estado r={r} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 px-2 pb-24 sm:px-0">
        <PageHeader
          icon={Inbox}
          title="Mis peticiones"
          subtitle="Lo que nos has pedido —arreglos, mejoras, dudas—, en qué va y qué se hizo."
          className="animate-fade-in-up"
        />
        {cuerpo}
      </div>
    </Layout>
  );
};

// Quién la usa lo decide el servidor (Carlos 11-oct-2026); quien no puede y entra por el enlace ve a quién pedírselo.
const ClientRequestsPage: React.FC = () => {
  const { puede } = useClientRequestsInfo();
  if (puede) return <ClientRequestsInner />;
  if (puede === null) return <Layout><div /></Layout>;
  return (
    <Layout>
      <div className="space-y-6 px-2 pb-24 sm:px-0">
        <PageHeader icon={Inbox} title="Mis peticiones" subtitle="Lo que tu negocio nos ha pedido a Lealify." className="animate-fade-in-up" />
        <p className="rounded-xl border border-line bg-surface p-4 text-sm text-content-2" data-testid="peticiones-sin-permiso">
          Las peticiones a Lealify las lleva el dueño o encargado del negocio. Si necesitas algo, pídeselo a él o a ella.
        </p>
      </div>
    </Layout>
  );
};

export default ClientRequestsPage;
